import { useEffect, useRef, useState } from 'react';

/**
 * Linha nova (v2.1, p.21, R05): "entra com tinta laranja, que se apaga até o
 * branco da tabela", em 1,2 s (`--mov-linha-nova`, classe `linha-nova`).
 *
 * Nova é a linha que ENTROU numa lista que já estava na tela — o lançamento
 * que acabou de ser gravado, a máquina que chegou pelo recarregar. Não é nova
 * a lista que trocou inteira (outra página, outro filtro) nem a que acabou de
 * carregar: pintar tudo de laranja não diz nada. A regra:
 *
 * - havia lista antes, e com linha;
 * - pelo menos uma linha de antes continua (a lista não trocou inteira);
 * - entraram no máximo duas — mais que isso é outra lista, não um lançamento.
 */
export const MAXIMO_DE_LINHAS_NOVAS = 2;

export function linhasQueEntraram(antes: readonly string[] | null, agora: readonly string[]): readonly string[] {
  if (!antes || antes.length === 0) return [];
  const deAntes = new Set(antes);
  const entraram = agora.filter((chave) => !deAntes.has(chave));
  const ficaram = agora.length - entraram.length;
  if (entraram.length === 0 || ficaram === 0 || entraram.length > MAXIMO_DE_LINHAS_NOVAS) return [];
  return entraram;
}

/** Quanto a marca dura: o tempo do `--mov-linha-nova`, para a classe sair depois de a tinta se apagar. */
const DURACAO_MS = 1_200;

/** As chaves das linhas que acabaram de entrar, por 1,2 s. */
export function useLinhasNovas(chaves: readonly string[]): ReadonlySet<string> {
  const [novas, setNovas] = useState<ReadonlySet<string>>(() => new Set());
  const anteriores = useRef<readonly string[] | null>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assinatura = chaves.join('\u0000');

  useEffect(() => {
    const atuais = assinatura === '' ? [] : assinatura.split('\u0000');
    const entraram = linhasQueEntraram(anteriores.current, atuais);
    anteriores.current = atuais;
    if (entraram.length === 0) return;
    setNovas(new Set(entraram));
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = setTimeout(() => {
      setNovas(new Set());
    }, DURACAO_MS);
  }, [assinatura]);

  useEffect(
    () => () => {
      if (relogio.current) clearTimeout(relogio.current);
    },
    [],
  );

  return novas;
}
